import { AlertTriangle, ArrowRight, Check, ClipboardCopy, Info, Lightbulb } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { HandbookDiagram } from './HandbookDiagrams';
import { resolveHandbookHref } from './handbookContent';
import type { Block, CalloutTone } from './handbookTypes';
import { parseInline, stepAnchor } from './inlineMarkup';

export function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((token, index) => {
        if (token.kind === 'code') {
          return (
            <code key={index} className="rounded border border-border bg-muted/50 px-1 py-px font-mono text-[0.85em] text-foreground [overflow-wrap:anywhere]">
              {token.value}
            </code>
          );
        }
        if (token.kind === 'strong') {
          return (
            <strong key={index} className="font-semibold text-foreground">
              {token.value}
            </strong>
          );
        }
        if (token.kind === 'link') {
          const className = 'font-medium text-brand underline-offset-4 hover:underline';
          const target = resolveHandbookHref(token.href);
          return target.internal ? (
            <Link key={index} to={target.to} className={className}>
              {token.value}
            </Link>
          ) : (
            <a key={index} href={target.href} target="_blank" rel="noreferrer" className={className}>
              {token.value}
            </a>
          );
        }
        return <Fragment key={index}>{token.value}</Fragment>;
      })}
    </>
  );
}

const CALLOUT_STYLES: Record<CalloutTone, { icon: typeof Info; box: string; iconClass: string }> = {
  info: { icon: Info, box: 'border-brand/30 bg-brand-muted/20', iconClass: 'text-brand' },
  success: { icon: Check, box: 'border-success/30 bg-success-bg/40', iconClass: 'text-success' },
  warning: { icon: AlertTriangle, box: 'border-warning/30 bg-warning-bg/40', iconClass: 'text-warning' },
  note: { icon: Lightbulb, box: 'border-border bg-card/60', iconClass: 'text-muted-foreground' },
};

function CodeBlock({ code, title, lang }: { code: string; title?: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="group my-5 overflow-hidden rounded-xl border border-border bg-background">
      <div className="flex items-center justify-between border-b border-border bg-card/60 px-4 py-2">
        <span className="truncate font-mono text-[11px] text-muted-foreground">{title ?? lang ?? 'text'}</span>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label="Copy code"
          className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          {copied ? <Check className="h-3 w-3 text-success" /> : <ClipboardCopy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[12px] leading-6 text-foreground/90">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function HandbookBlock({ block, nested }: { block: Block; nested?: boolean }) {
  switch (block.t) {
    case 'p':
      return (
        <p className="my-4 text-[15px] leading-7 text-muted-foreground">
          <Inline text={block.text} />
        </p>
      );
    case 'h2':
      return (
        <h2 id={block.id} className="group mb-3 mt-12 scroll-mt-24 text-xl font-semibold tracking-tight text-foreground">
          <a href={`#${block.id}`} className="no-underline">
            {block.text}
            <span className="ml-2 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">#</span>
          </a>
        </h2>
      );
    case 'h3':
      return <h3 className="mb-2 mt-8 text-base font-semibold text-foreground">{block.text}</h3>;
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag className={cn('my-4 space-y-2 pl-5 text-[15px] leading-7 text-muted-foreground', block.ordered ? 'list-decimal' : 'list-disc marker:text-brand')}>
          {block.items.map((item, index) => (
            <li key={index} className="pl-1">
              <Inline text={item} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'code':
      return <CodeBlock code={block.code} title={block.title} lang={block.lang} />;
    case 'callout': {
      const style = CALLOUT_STYLES[block.tone];
      const Icon = style.icon;
      return (
        <div className={cn('my-6 flex gap-3 rounded-xl border p-4', style.box)}>
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', style.iconClass)} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{block.title}</div>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              <Inline text={block.text} />
            </p>
          </div>
        </div>
      );
    }
    case 'steps':
      return (
        <ol className="my-8 space-y-0">
          {block.items.map((item, index) => (
            <li key={item.title} id={nested ? undefined : stepAnchor(index, item.title)} className="relative flex scroll-mt-24 gap-5 pb-8 last:pb-0">
              {index < block.items.length - 1 ? <span className="absolute left-[15px] top-9 h-[calc(100%-2.25rem)] w-px bg-border" aria-hidden="true" /> : null}
              <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand/40 bg-brand-muted font-mono text-xs font-semibold text-foreground">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1 pt-1">
                <div className="text-base font-semibold text-foreground">{item.title}</div>
                <div className="[&>*:first-child]:mt-2">
                  {item.blocks.map((child, childIndex) => (
                    <HandbookBlock key={childIndex} block={child} nested />
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div className={cn('my-6 overflow-x-auto rounded-xl border border-border', nested && 'my-4')}>
          <table className="w-full text-left text-sm">
            <thead className="bg-card/60">
              <tr>
                {block.head.map((cell, index) => (
                  <th key={index} className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-foreground">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t border-border align-top">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className={cn('px-4 py-2.5 leading-6', cellIndex === 0 ? 'text-foreground' : 'text-muted-foreground')}>
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'diagram':
      return <HandbookDiagram kind={block.kind} caption={block.caption} />;
    case 'cards':
      return (
        <div className="my-6 grid gap-3 sm:grid-cols-2">
          {block.items.map((item) => {
            const target = resolveHandbookHref(item.to);
            const className = 'group rounded-xl border border-border bg-card/40 p-4 transition-colors hover:border-brand/40 hover:bg-card';
            const body = (
              <>
                <div className="flex items-center justify-between gap-2 text-sm font-semibold text-foreground">
                  {item.title}
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{item.text}</p>
              </>
            );
            return target.internal ? (
              <Link key={item.to} to={target.to} className={className}>
                {body}
              </Link>
            ) : (
              <a key={item.to} href={target.href} target="_blank" rel="noreferrer" className={className}>
                {body}
              </a>
            );
          })}
        </div>
      );
    case 'definitions':
      return (
        <dl className="my-6 divide-y divide-border rounded-xl border border-border">
          {block.items.map((item) => (
            <div key={item.term} className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4">
              <dt className="text-sm font-semibold text-foreground">{item.term}</dt>
              <dd className="text-sm leading-6 text-muted-foreground">
                <Inline text={item.text} />
              </dd>
            </div>
          ))}
        </dl>
      );
  }
}
